import { type JSX, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { useDeleteKnowledgeSource } from '@/lib/api/workspace-knowledge.js';
import { notify } from '@/lib/notify.js';

import { oneDriveKnowledgeRemovalConfirmation } from './onedrive-knowledge.js';

/**
 * Confirms taking a connected OneDrive folder back out of the workspace's knowledge, and does it.
 *
 * It asks first, unlike the knowledge tab's own Remove. That list is where somebody goes to curate the
 * shelf; this button lives in a file-management screen, one place along from Settings and Remove folder,
 * where the click is easy to make by accident and what it destroys — the index built from the folder —
 * does not come back on its own.
 *
 * The delete goes through the ordinary workspace route, so a failure the service reports is the one
 * shown, and the dialog stays open with it. The list refresh is the mutation's own, which is what flips
 * the row back to offering the folder again.
 */
export function OneDriveKnowledgeRemoveDialog({
  workspaceId,
  sourceId,
  name,
  isFile = false,
  onClose,
}: {
  workspaceId: string;
  /** The knowledge source registered for this folder's root — resolved before the dialog opens. */
  sourceId: string;
  name: string;
  /** Whether the scope is one file, which has no inside for the confirmation to warn about. */
  isFile?: boolean;
  onClose: () => void;
}): JSX.Element {
  const removeSource = useDeleteKnowledgeSource(workspaceId);
  const [error, setError] = useState<string | null>(null);
  const { title, description } = oneDriveKnowledgeRemovalConfirmation(
    name,
    isFile,
  );

  async function confirm(): Promise<void> {
    setError(null);
    try {
      await removeSource.mutateAsync(sourceId);
      notify.success('Removed from the knowledge base');
      onClose();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : 'Could not remove this folder from the knowledge base.',
      );
    }
  }

  return (
    <AppDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={title}
      description={description}
    >
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <Button
          variant="ghost"
          onClick={onClose}
          disabled={removeSource.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={removeSource.isPending}
          onClick={() => void confirm()}
        >
          {removeSource.isPending ? 'Removing…' : 'Remove from knowledge'}
        </Button>
      </div>
    </AppDialog>
  );
}
